/* eslint-disable security/detect-object-injection */
/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { getPayloadObject, getTelemetryAttributes, onTelemetryClick } from '@msdyn365-commerce-modules/utilities';
import * as Msdyn365 from '@msdyn365-commerce/core';
import classnames from 'classnames';
import * as React from 'react';

import { ILxsContentCardLinks } from '../lxs-2-column-content-block.data';
import { ILinksData, LinksIcon } from '../lxs-2-column-content-block.props.autogenerated';

export class LxsContentCardLinks extends React.PureComponent<ILxsContentCardLinks> {
    public render(): JSX.Element {
        const { CLASS_NAME } = this.props;
        const editableLinks = this._mapEditableLinks(this.props.links, CLASS_NAME);

        return (
            <div
                className={classnames(`${CLASS_NAME}__cta`, {
                    [`${CLASS_NAME}__cta--button`]: this.props.variant === 'button',
                    [`${CLASS_NAME}__cta--links`]: this.props.variant !== 'button'
                })}
            >
                {editableLinks && editableLinks.length > 0
                    ? editableLinks.map((editableLink, index) => this._renderLink(editableLink, index))
                    : null}
            </div>
        );
    }

    private readonly _renderLink = (editableLink: Msdyn365.ILinksData, index: number): JSX.Element => {
        const { CLASS_NAME } = this.props;
        const originalLink = this.props.links[index];
        const iconName = originalLink.icon || this.props.defaultIcon || LinksIcon.none;

        return (
            <span
                key={`${editableLink.linkText || 'link'}-${index}`}
                className={classnames(`${CLASS_NAME}__cta-item`, {
                    [`${CLASS_NAME}__cta-item--button`]: this.props.variant === 'button',
                    [`${CLASS_NAME}__cta-item--icon-${iconName}`]: iconName && iconName !== LinksIcon.none
                })}
            >
                <Msdyn365.Link
                    link={editableLink}
                    editProps={{
                        onTextChange: this.props.onTextChange ? this.props.onTextChange(index) : undefined,
                        requestContext: this.props.requestContext
                    }}
                />
            </span>
        );
    };

    private readonly _mapEditableLinks = (linkdata: ILinksData[], CLASS_NAME: string): Msdyn365.ILinksData[] | null => {
        if (!linkdata || linkdata.length === 0) {
            return null;
        }

        const editableLinks: Msdyn365.ILinksData[] = [];

        linkdata.forEach((item, index) => {
            const link = item.link;

            if (!link || !link.linkUrl || !link.linkUrl.destinationUrl) {
                return;
            }

            const payLoad = getPayloadObject('click', this.props.telemetryContent, '', '');
            const linkText = link.linkText ? link.linkText : '';
            payLoad.contentAction.etext = linkText;

            const attributes = getTelemetryAttributes(this.props.telemetryContent, payLoad);
            const isButton = this.props.variant === 'button';
            const btnClass = isButton ? `msc-cta__primary ${CLASS_NAME}__cta-button` : `${CLASS_NAME}__cta-link`;

            const editableLink: Msdyn365.ILinksData = {
                ariaLabel: link.ariaLabel,
                className: btnClass,
                linkText: link.linkText,
                linkUrl: link.linkUrl.destinationUrl,
                openInNewTab: link.openInNewTab,
                role: this.props.role,
                additionalProperties: attributes,
                onClick: onTelemetryClick(this.props.telemetryContent, payLoad, linkText)
            };

            editableLinks.push(editableLink);
        });

        return editableLinks;
    };
}

export default LxsContentCardLinks;
